import * as THREE from "three";

/**
 * Engine owns the low-level Three.js plumbing: renderer, scene, lighting,
 * clock, and resize. applyEnvironment() lets each theme restyle the world
 * (background, fog, light colors) when a new floor loads.
 */
export class Engine {
  constructor(canvas) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0d0f14);
    this.scene.fog = new THREE.Fog(0x0d0f14, 30, 70);
    this.clock = new THREE.Clock();

    this._setupLights();
    this._onResize();
    window.addEventListener("resize", () => this._onResize());
  }

  _setupLights() {
    this.ambient = new THREE.AmbientLight(0x8899bb, 0.55);
    this.hemi = new THREE.HemisphereLight(0xbfd4ff, 0x2a2016, 0.5);
    this.key = new THREE.DirectionalLight(0xffd9a0, 1.15);
    this.key.position.set(20, 34, 14);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(2048, 2048);
    const s = 42;
    Object.assign(this.key.shadow.camera, { left: -s, right: s, top: s, bottom: -s, near: 1, far: 120 });
    this.key.shadow.bias = -0.0005;
    this.scene.add(this.ambient, this.hemi, this.key, this.key.target);
  }

  /** Restyle for a theme's env: { background, fog:[c,near,far], ambient:[c,i], hemi:[sky,ground,i], key:[c,i] }. */
  applyEnvironment(env) {
    if (!env) return;
    this.scene.background = new THREE.Color(env.background);
    if (env.fog) { this.scene.fog.color.set(env.fog[0]); this.scene.fog.near = env.fog[1]; this.scene.fog.far = env.fog[2]; }
    if (env.ambient) { this.ambient.color.set(env.ambient[0]); this.ambient.intensity = env.ambient[1]; }
    if (env.hemi) { this.hemi.color.set(env.hemi[0]); this.hemi.groundColor.set(env.hemi[1]); this.hemi.intensity = env.hemi[2]; }
    if (env.key) { this.key.color.set(env.key[0]); this.key.intensity = env.key[1]; }
  }

  focusLight(x, z) {
    this.key.position.set(x + 20, 34, z + 14);
    this.key.target.position.set(x, 0, z);
  }

  _onResize() {
    const w = window.innerWidth, h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.aspect = w / h;
    if (this.onResize) this.onResize(this.aspect);
  }

  render(camera) { this.renderer.render(this.scene, camera); }
}
